import { useState, useEffect, useCallback } from "react";
import { FiArrowDown, FiArrowUp, FiX, FiCheck } from "react-icons/fi";
import { apiGet, apiPost } from "../api";
import { PageHeader, Card, StatCard, Btn, THead, TD, Icon, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });

const METHOD_LABEL = {
  CASH: "Cash",
  BANK_TRANSFER: "Bank transfer",
  CHECK: "Check",
  OTHER: "Other",
};

/* ─── Record Payment modal ─── */
function RecordPaymentModal({ token, customers, onClose, onSaved }) {
  const [form, setForm] = useState({ customer: "", amount: "", payment_method: "CASH", note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSave = form.customer && form.amount && Number(form.amount) > 0 && !saving;

  const selected = customers.find(c => String(c.id) === String(form.customer));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      const res = await apiPost("/api/inventory/payments", {
        customer: form.customer,
        amount: parseFloat(form.amount),
        payment_method: form.payment_method,
        note: form.note.trim(),
      }, token, "employee-token");

      if (res.ok) { onSaved(await res.json()); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to record payment."); }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const withBalance = customers.filter(c => Number(c.balance) > 0);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 440, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--success-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="payments" size={21} stroke="var(--success)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>Record Payment</span>
                <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>ငွေပေးချေမှု</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>Customer paying off their credit balance.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* customer */}
          <div>
            <label style={lbl}>Customer · <span style={{ color: "var(--danger)" }}>required</span></label>
            <div style={fieldStyle}>
              <select value={form.customer} onChange={e => set("customer", e.target.value)} required
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: form.customer ? "var(--ink)" : "var(--n-400)", cursor: "pointer" }}>
                <option value="">Select customer…</option>
                {withBalance.map(c => (
                  <option key={c.id} value={c.id}>{c.name} — {MMK(c.balance)} Ks owed</option>
                ))}
              </select>
            </div>
            {selected && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warning)", fontWeight: 600 }}>
                Current balance: {MMK(selected.balance)} Ks
              </div>
            )}
          </div>

          {/* amount */}
          <div>
            <label style={lbl}>Amount</label>
            <div style={{ ...fieldStyle, gap: 8 }}>
              <input type="number" value={form.amount} onChange={e => set("amount", e.target.value)}
                placeholder="0" min="1" required
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)", textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
              <span style={{ fontSize: 13, color: "var(--n-400)", fontWeight: 600, whiteSpace: "nowrap" }}>Ks</span>
            </div>
          </div>

          {/* payment method */}
          <div>
            <label style={lbl}>Payment method</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["CASH","Cash"],["BANK_TRANSFER","Bank"],["OTHER","Other"]].map(([m, t]) => (
                <button key={m} type="button" onClick={() => set("payment_method", m)}
                  style={{ flex: 1, padding: "9px", borderRadius: "var(--r-sm)", cursor: "pointer", border: `1px solid ${form.payment_method === m ? "var(--primary)" : "var(--line)"}`, background: form.payment_method === m ? "var(--blue-50)" : "var(--surface)", color: form.payment_method === m ? "var(--primary)" : "var(--n-600)", fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 13.5 }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* note */}
          <div>
            <label style={lbl}>Note <span style={{ fontWeight: 500, color: "var(--n-400)" }}>optional</span></label>
            <div style={fieldStyle}>
              <input value={form.note} onChange={e => set("note", e.target.value)}
                placeholder="Reference number, bank slip, etc."
                style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)" }} />
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSave}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSave ? "pointer" : "not-allowed", background: canSave ? "var(--primary)" : "var(--n-200)", color: "#fff" }}>
            <FiCheck size={16} /> {saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

const lbl = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 6 };
const fieldStyle = { display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)" };

/* ─── Main ─── */
export default function Payments({ token, go }) {
  const [payments, setPayments]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [recording, setRecording] = useState(false);
  const [page, setPage]           = useState(1);

  const fetchAll = useCallback(async () => {
    try {
      const [pr, cr] = await Promise.all([
        apiGet("/api/inventory/payments", token).then(r => r.json()),
        apiGet("/api/inventory/customers", token).then(r => r.json()),
      ]);
      setPayments(Array.isArray(pr) ? pr : []);
      setCustomers(Array.isArray(cr) ? cr : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* 7-day stats — all current payments are "in" (customer receipts) */
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recent = payments.filter(p => new Date(p.created_at) >= sevenDaysAgo);
  const inSum  = recent.reduce((s, p) => s + Number(p.amount), 0);
  const outSum = 0; // supplier payments not yet in backend
  const net    = inSum - outSum;

  const handleSaved = (payment) => {
    setPayments(prev => [payment, ...prev]);
    // Refresh customers to get updated balances
    apiGet("/api/inventory/customers", token).then(r => r.json()).then(d => {
      if (Array.isArray(d)) setCustomers(d);
    }).catch(() => {});
    setRecording(false);
  };

  return (
    <div>
      <PageHeader title="Payments" mm="ငွေပေးချေမှု" desc="Money in from customers · money out to suppliers">
        <Btn kind="ghost" icon="download" sm>Export</Btn>
        <Btn kind="pri" icon="plus" onClick={() => setRecording(true)}>Record Payment</Btn>
      </PageHeader>

      {/* 3 stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 18 }}>
        <StatCard
          label="Money In (7d)"
          value={(inSum / 1e6).toFixed(2) + "M"}
          unit="MMK"
          icon="arrowDown"
          tone="green"
          sub={MMK(inSum) + " Ks"}
        />
        <StatCard
          label="Money Out (7d)"
          value={(outSum / 1e6).toFixed(2) + "M"}
          unit="MMK"
          icon="arrowUp"
          tone="amber"
          sub={MMK(outSum) + " Ks"}
        />
        <StatCard
          label="Net Flow"
          value={((net < 0 ? -net : net) / 1e6).toFixed(2) + "M"}
          unit="MMK"
          icon="payments"
          tone="blue"
          sub={(net >= 0 ? "+" : "") + MMK(net) + " Ks"}
        />
      </div>

      {/* table */}
      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--sub)", fontSize: 14 }}>Loading…</div>
        ) : (
          <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={[
              { t: "Date" }, { t: "Direction" }, { t: "Party" },
              { t: "Method" }, { t: "Reference" }, { t: "Amount", r: 1 },
            ]} />
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "34px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                    No payments recorded yet.
                  </td>
                </tr>
              )}
              {payments.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT).map(p => {
                const isIn = true; // all current payments are customer receipts (money in)
                return (
                  <tr key={p.id}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <TD mut>{fmtDate(p.created_at)}</TD>

                    {/* direction */}
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 700, color: isIn ? "var(--success)" : "var(--warning)" }}>
                        {isIn
                          ? <FiArrowDown size={15} color="var(--success)" />
                          : <FiArrowUp size={15} color="var(--warning)" />}
                        {isIn ? "In" : "Out"}
                      </span>
                    </td>

                    <TD b>{p.customer_name || "—"}</TD>
                    <TD mut>{METHOD_LABEL[p.payment_method] || p.payment_method}</TD>
                    <TD mut>{p.note || "—"}</TD>
                    <TD r b style={{ color: isIn ? "var(--success)" : "var(--ink)" }}>
                      {isIn ? "+" : "–"}{MMK(p.amount)}
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={payments.length} page={page} onChange={setPage} />
          </>
        )}
      </Card>

      {recording && (
        <RecordPaymentModal
          token={token}
          customers={customers}
          onClose={() => setRecording(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
